import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import RoutyPanel, { type RoutyPendingPrompt } from '../components/routy/RoutyPanel';
import RoutyMark from '../components/routy/RoutyMark';
import { useIsAnyModalOpen } from '../hooks/useIsAnyModalOpen';
import { useIntercom } from '../components/shared/IntercomProvider';
import { config } from '../config/config';

interface RoutyContextValue {
  isOpen: boolean;
  /** False when Routy is feature-flagged off, so callers can hide their entry points. */
  isAvailable: boolean;
  openRouty: () => void;
  /**
   * Open Routy and send `text` as if the operator had typed it. For handing a
   * question over from elsewhere in the app (e.g. "why can't I book this
   * day?" from the slot picker) so they land on the answer, not a blank chat.
   *
   * `display` is what the transcript shows. Hand-off text is written for the
   * model — request ids, booking form ids, tool names — and dumping that into
   * the chat as if the operator typed it is machinery they never asked to see.
   */
  askRouty: (text: string, display?: string) => void;
  closeRouty: () => void;
  toggleRouty: () => void;
}

const RoutyContext = createContext<RoutyContextValue | undefined>(undefined);

/**
 * Access the global Routy assistant panel from anywhere in the tenant app.
 *
 *   const { openRouty } = useRouty();
 *   <button onClick={openRouty}>Ask Routy</button>
 *
 * Returns no-ops outside the provider so non-tenant routes don't crash.
 */
export function useRouty(): RoutyContextValue {
  const ctx = useContext(RoutyContext);
  if (!ctx) {
    return {
      isOpen: false,
      isAvailable: false,
      openRouty: () => {},
      askRouty: () => {},
      closeRouty: () => {},
      toggleRouty: () => {},
    };
  }
  return ctx;
}

interface RoutyProviderProps {
  children: React.ReactNode;
}

/**
 * Mounts the single global Routy assistant: a floating launcher button plus the
 * slide-in chat panel. Follows the UpgradePromptProvider pattern so any
 * descendant can open it via useRouty().
 *
 * Sits inside the tenant provider tree so the panel can read the current user
 * (AppConfig) and company timezone (CompanyData).
 */
export const RoutyProvider: React.FC<RoutyProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  // `data-routy-ui` on the launcher/panel keeps this from ever flagging
  // Routy's own (expanded) panel as "another modal" — see the hook's docs.
  const isAnyModalOpen = useIsAnyModalOpen();
  // Also step aside when the Intercom "Help" messenger is open — otherwise
  // the two floating widgets overlap in the bottom-right corner.
  const { isIntercomOpen } = useIntercom();
  const hideLauncher = isAnyModalOpen || isIntercomOpen;

  // A question handed over from another surface, waiting for the panel to be
  // ready to run it. Cleared as soon as the panel sends it.
  const [pendingPrompt, setPendingPrompt] = useState<RoutyPendingPrompt | null>(null);

  const openRouty = useCallback(() => setIsOpen(true), []);
  const closeRouty = useCallback(() => setIsOpen(false), []);
  const toggleRouty = useCallback(() => setIsOpen((v) => !v), []);

  const askRouty = useCallback((text: string, display?: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // A fresh id every time, so asking the same question twice sends twice
    // instead of being swallowed as an already-consumed prompt.
    setPendingPrompt({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      text: trimmed,
      display: display?.trim() || undefined,
    });
    setIsOpen(true);
  }, []);

  const clearPendingPrompt = useCallback(() => setPendingPrompt(null), []);

  const value = useMemo<RoutyContextValue>(
    () => ({ isOpen, isAvailable: config.routy.enabled, openRouty, askRouty, closeRouty, toggleRouty }),
    [isOpen, openRouty, askRouty, closeRouty, toggleRouty],
  );

  // Feature-gated: while the browser prototype is being replaced by the backend
  // agent, Routy stays hidden in production (VITE_ROUTY_ENABLED=false). We still
  // provide the (no-op) context so any current/future `useRouty()` callers are
  // safe, but render no launcher and no panel — nothing to see, no key needed.
  if (!config.routy.enabled) {
    return <RoutyContext.Provider value={value}>{children}</RoutyContext.Provider>;
  }

  return (
    <RoutyContext.Provider value={value}>
      {children}

      {/* Floating launcher, bottom-right corner. Intercom's own launcher is
          hidden app-wide (see IntercomProvider) — support now lives in the
          sidebar "Help" button — so this owns the corner outright.
          Faded out (not unmounted, so it animates smoothly) while another
          modal/drawer is open, or while the Intercom "Help" messenger is open,
          so it doesn't float on top of that content. */}
      <button
        data-routy-ui="true"
        onClick={toggleRouty}
        aria-label={isOpen ? 'Close Routy' : 'Ask Routy'}
        aria-hidden={hideLauncher}
        tabIndex={hideLauncher ? -1 : 0}
        className={`fixed bottom-5 right-5 z-[9997] w-14 h-14 rounded-xl bg-ink-950 text-white shadow-lg shadow-ink-950/30 flex items-center justify-center hover:bg-ink-900 hover:scale-105 active:scale-95 transition-all duration-200 ${
          hideLauncher ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100'
        }`}
      >
        {isOpen ? <X className="w-6 h-6" /> : <RoutyMark className="w-8 h-8" bgClassName="bg-transparent" textClassName="text-lg" />}
      </button>

      <RoutyPanel
        isOpen={isOpen}
        onClose={closeRouty}
        pendingPrompt={pendingPrompt}
        onPromptConsumed={clearPendingPrompt}
      />
    </RoutyContext.Provider>
  );
};
